import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";

export type ToastVariant = "info" | "success" | "warning" | "error";

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

/** Event name the toaster listens for; dispatched by the `toast()` helper. */
const TOAST_EVENT = "bn-toast";

/**
 * toast shows a transient notification. Mount a single <bn-toaster> in the app
 * shell; calling toast() from anywhere dispatches a window event it picks up.
 *
 *   toast("Workspace created", "success");
 *   toast(humanError(err), "error");
 */
export function toast(message: string, variant: ToastVariant = "info", durationMs = 4000): void {
  window.dispatchEvent(
    new CustomEvent(TOAST_EVENT, { detail: { message, variant, durationMs } }),
  );
}

/**
 * bn-toaster is the fixed, bottom-right stack that renders toasts dispatched via
 * toast(). Place one instance in the app shell. Each toast auto-dismisses after
 * its duration and is click-to-dismiss.
 */
@customElement("bn-toaster")
export class BnToaster extends LitElement {
  @state() private items: ToastItem[] = [];
  private seq = 0;

  private onToast = (e: Event) => {
    const detail = (e as CustomEvent).detail as {
      message: string;
      variant: ToastVariant;
      durationMs: number;
    };
    const id = ++this.seq;
    this.items = [...this.items, { id, message: detail.message, variant: detail.variant }];
    window.setTimeout(() => this.dismiss(id), detail.durationMs);
  };

  override connectedCallback() {
    super.connectedCallback();
    window.addEventListener(TOAST_EVENT, this.onToast);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(TOAST_EVENT, this.onToast);
  }

  private dismiss(id: number) {
    this.items = this.items.filter((t) => t.id !== id);
  }

  static override styles = css`
    :host {
      position: fixed;
      bottom: var(--bino-space-lg);
      right: var(--bino-space-lg);
      z-index: var(--bino-z-modal);
      display: flex;
      flex-direction: column;
      gap: var(--bino-space-sm);
      pointer-events: none;
    }
    .toast {
      pointer-events: auto;
      min-width: 16rem;
      max-width: 26rem;
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
      padding: 0.625rem 0.875rem;
      border-radius: var(--bino-radius);
      background: var(--bino-surface);
      border: 1px solid var(--bino-border);
      border-left-width: 3px;
      box-shadow: var(--bino-shadow-dropdown);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
      color: var(--bino-text);
      cursor: pointer;
      animation: slide-in var(--bino-transition-normal);
    }
    .toast.info {
      border-left-color: var(--bino-info);
    }
    .toast.success {
      border-left-color: var(--bino-success);
    }
    .toast.warning {
      border-left-color: var(--bino-warning);
    }
    .toast.error {
      border-left-color: var(--bino-error);
    }
    .msg {
      flex: 1;
      min-width: 0;
      word-break: break-word;
    }
    @keyframes slide-in {
      from {
        opacity: 0;
        transform: translateY(8px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @media (prefers-reduced-motion: reduce) {
      .toast {
        animation: none;
      }
    }
  `;

  override render() {
    return html`${this.items.map(
      (t) => html`<div
        class="toast ${t.variant}"
        role="status"
        @click=${() => this.dismiss(t.id)}
      >
        <span class="msg">${t.message}</span>
      </div>`,
    )}`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bn-toaster": BnToaster;
  }
}
